<script setup>
import {Icon} from "@iconify/vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {computed, onMounted, provide, ref, reactive, onBeforeUnmount, onUnmounted} from "vue";
import {useMarketMapStore} from "@/stores/marketMap.js";
import US from "@/assets/images/flags/US.png"
import EU from "@/assets/images/flags/EU.png"
import {showMessage} from "@/utils/showMessage.js";
import ChangePriceModal from "@/views/watchdog/marketMap/ChangePriceModal.vue";
import AddStoreModal from "@/views/watchdog/marketMap/AddStoreModal.vue";
import UpdateStoreModal from "@/views/watchdog/marketMap/UpdateStoreModal.vue";
import SettingsModal from "@/views/watchdog/marketMap/SettingsModal.vue";
import AddMapModal from "@/views/watchdog/marketMap/addModal.vue"
import {useUserStore} from "@/stores/user.js";
import Button from "@/components/Button/index.vue";
import FiltersModal from "@/views/watchdog/marketMap/FiltersModal.vue";
import {Menu} from "primevue";
import {useConfirm} from "primevue/useconfirm";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";

const {
  saving,
  deleting,
  setSaving,
  setDeleting,
} = useLoadingFlags()
const marketMapStore = useMarketMapStore();
const userStore = useUserStore();
const confirm = useConfirm();
const keyword = ref('')
const loading = ref(true)
const isFetching = ref(false)
const columnsCount = ref(1)
const isShowChangePriceModal = ref(false)
const isShowAddStoreModal = ref(false)
const isShowUpdateStoreModal = ref(false)
const isShowSettingsModal = ref(false)
const isShowFiltersModal = ref(false);
const isShowAddMapModal = ref(false);
const marketMaps = ref([])
const selectedItem = ref(null)
const selectedStore = ref(null)
const filteredOffers = computed(() =>
    marketMapStore.getFilteredOffers(marketMaps.value, keyword.value)
)
const canChangePrice = computed(() => userStore.hasPermission("MANAGE_G2G_OFFERS") || userStore.hasPermission('BIG_BOSS'))
const canAddStore = computed(() => userStore.hasPermission("MANAGE_G2G_OFFERS") || userStore.hasPermission('BIG_BOSS'))
const offerMenus = reactive({})
const pollInterval = ref(null)
const isMyStore = (name) => {
  const store = name.toLowerCase()

  return store.includes('dealhub') ||
      store.includes('incepto')
}

const loadOffers = async (showLoading = true) => {
  if (showLoading) loading.value = true

  try {
    //loading.value = true
    const res = await marketMapStore.action_getAllMarketMaps4()
    if (res.result === true) {
      marketMaps.value = res.data
    } else {
      //showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    loading.value = false
  }
}
const fetchInitialData = async () => {
  await loadOffers(true)
}
const refreshData = async () => {
  if (isFetching.value) return

  isFetching.value = true
  try {
    await loadOffers(false)
  } finally {
    isFetching.value = false
  }
}
const startPolling = () => {
  if (pollInterval.value) return
  pollInterval.value = setInterval(refreshData, 10000)
}
const stopPolling = () => {
  if (pollInterval.value) {
    clearInterval(pollInterval.value)
    pollInterval.value = null
  }
}

const showAddStoreModal = (selected) => {
  selectedItem.value = selected
  isShowAddStoreModal.value = true
}

const showUpdateStoreModal = (selectedOffer, selectedStoree) => {
  selectedItem.value = selectedOffer
  selectedStore.value = selectedStoree
  isShowUpdateStoreModal.value = true
}

const showChangePriceModal = (selected, store) => {
  selectedItem.value = selected
  selectedStore.value = store
  isShowChangePriceModal.value = true
}

const showSettingsModal = (selected) => {
  selectedItem.value = selected
  isShowSettingsModal.value = true
}

const showFiltersModal = () => {
  isShowFiltersModal.value = true
}

const showAddMapModal = () => {
  isShowAddMapModal.value = true
}

const removeOffer = (offer) => {
  confirm.require({
    group: 'templating',
    header: 'Confirmation',
    message: {
      title: `Do you want to remove this offer?`,
      text: 'Are you sure?'
    },
    icon: 'pi pi-exclamation-circle',
    rejectProps: {
      class: '!border-slate-500 !py-2 !px-4 !text-slate-300',
      label: 'Cancel',
      outlined: true,
      size: 'small'
    },
    acceptProps: {
      class: '!border-blue-500 !py-2 !px-4 !text-blue-500 hover:!bg-blue-500 hover:!text-slate-200',
      outlined: true,
      label: 'Remove',
      size: 'small'
    },
    accept: async () => {
      try {
        setDeleting(true)
        const res = await marketMapStore.action_removeMarket4({
          "game": offer.game,
          "server": offer.server
        })
        if (res.result === true) {
          showMessage('Offer Removed.', 'success');
          await refreshData()
        } else {
          showMessage(res, 'error');
        }
      } catch (err) {
        showMessage(err, 'error');
      } finally {
        setDeleting(false)
      }
    },
    reject: () => {
      return
    }
  });
}

const getOfferMenuItems = (m) => {
  let items = [
    {label: 'Settings', icon: 'pi pi-pencil', command: () => showSettingsModal(m)},
    {label: 'Remove', icon: 'pi pi-pencil', command: () => removeOffer(m)},
  ]
  if (canAddStore.value) {
    items.push({label: 'Add Store', icon: 'pi pi-pencil', command: () => showAddStoreModal(m)})
  }
  return items
}

const toggleOfferMenu = (event, id) => {
  offerMenus[id]?.toggle(event)
}

const removeStore = async (offer, store) => {
  const result = offer.stores.filter(item => item.store !== store.store)
  try {
    saving.value = true
    const res = await marketMapStore.action_setStore4({
      "game": offer.game,
      "server": offer.server,
      "stores": result,
    })
    if (res.result === true) {
      showMessage('Store Removed.', 'success');
      setTimeout(() => {
        visible.value = false
      }, 1000)
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}



function updateColumnsCount() {
  const w = window.innerWidth
  if (w <= 640) columnsCount.value = 1
  else if (w <= 768) columnsCount.value = 1
  else if (w <= 1024) columnsCount.value = 2
  else if (w <= 1280) columnsCount.value = 2
  else if (w <= 1536) columnsCount.value = 3
  else columnsCount.value = 4
}

onMounted(() => {
  updateColumnsCount()
  window.addEventListener('resize', updateColumnsCount)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateColumnsCount)
})


onMounted(async () => {
  await fetchInitialData()
  startPolling()
})
onBeforeUnmount(() => {
  stopPolling()
})

provide('isShowChangePriceModal', isShowChangePriceModal)
provide('isShowAddStoreModal', isShowAddStoreModal)
provide('isShowUpdateStoreModal', isShowUpdateStoreModal)
provide('isShowSettingsModal', isShowSettingsModal)
provide('isShowFiltersModal', isShowFiltersModal)
provide('isShowAddMapModal', isShowAddMapModal)
</script>

<template>
  <div class="flex flex-col gap-4 w-full h-full overflow-hidden">
    <div class="flex flex-row justify-between gap-4 w-full">
      <Breadcrumbs/>
      <div class="flex flex-row gap-2 justify-end">
        <div
            class="flex flex-row gap-2 items-center w-max h-[32px] bg-slate-900 border border-slate-700 text-slate-300 px-2 rounded-md">
          <span>
            <svg width="16" height="16" viewBox="0 0 24 24">
              <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="m21 21l-4.343-4.343m0 0A8 8 0 1 0 5.343 5.343a8 8 0 0 0 11.314 11.314"/>
            </svg>
          </span>
          <input v-model="keyword" type="text"
                 class="bg-transparent w-full block focus:outline-none h-full rounded-md text-sm">
          <button v-if="keyword"
                  @click="keyword = ''" class="hover:text-red-400">
            <svg width="16" height="16" viewBox="0 0 16 16">
              <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="m11.25 4.75l-6.5 6.5m0-6.5l6.5 6.5"/>
            </svg>
          </button>
        </div>
        <button @click="showFiltersModal" class="btn btn-outline-dark py-1 text-xs font-medium rounded-md h-[32px]"
                :class="{'border !border-blue-500 shadow-[0_0_20px_5px_rgba(59,130,246,0.6)]': marketMapStore.isFilterActive}">
          Filters {{ marketMapStore.isFilterActive ? '(' + marketMapStore.activeFilterCount + ')' : '' }}
        </button>
        <button @click="showAddMapModal" class="btn btn-outline-dark py-1 text-xs font-medium rounded-md h-[32px]">
          Add Map
        </button>
      </div>
    </div>
    <div v-if="loading" class="flex items-center justify-center h-60">
      <Icon icon="eos-icons:bubble-loading" class="size-12"/>
    </div>
    <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar text-slate-300 text-sm">
      <div class="grid gap-8 h-full pr-1 auto-rows-[calc((100%-2rem)/1.5)] xl:auto-rows-[calc((100%-2rem)/1.5)] 2xl:auto-rows-[calc((100%-2rem)/2)]"
           :style="{gridTemplateColumns: `repeat(${columnsCount}, minmax(0, 1fr))`}">
        <div v-for="(m, index) in filteredOffers"
             class="p-2 bg-slate-800 rounded-lg flex flex-col gap-2 border border-slate-600">
          <div class="flex flex-col gap-2 items-end">
            <div class="flex-1 flex flex-row justify-between items-start w-full gap-2">
              <div class="flex flex-col gap-2">
                <span class="text-blue-400 text-base">{{ m.server }}</span>
                <span class="text-xs">{{ m.game }}</span>
              </div>
              <div class="flex flex-col justify-between h-full">
                <div class="flex flex-row gap-2 justify-end">
                  <svg v-if="m.notifyChanges" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                       viewBox="0 0 36 36">
                    <path fill="currentColor"
                          d="M32.51 27.83A14.4 14.4 0 0 1 30 24.9a12.6 12.6 0 0 1-1.35-4.81v-4.94A10.81 10.81 0 0 0 19.21 4.4V3.11a1.33 1.33 0 1 0-2.67 0v1.31a10.81 10.81 0 0 0-9.33 10.73v4.94a12.6 12.6 0 0 1-1.35 4.81a14.4 14.4 0 0 1-2.47 2.93a1 1 0 0 0-.34.75v1.36a1 1 0 0 0 1 1h27.8a1 1 0 0 0 1-1v-1.36a1 1 0 0 0-.34-.75M5.13 28.94a16.2 16.2 0 0 0 2.44-3a14.2 14.2 0 0 0 1.65-5.85v-4.94a8.74 8.74 0 1 1 17.47 0v4.94a14.2 14.2 0 0 0 1.65 5.85a16.2 16.2 0 0 0 2.44 3Z"
                          class="clr-i-outline clr-i-outline-path-1"/>
                    <path fill="currentColor" d="M18 34.28A2.67 2.67 0 0 0 20.58 32h-5.26A2.67 2.67 0 0 0 18 34.28"
                          class="clr-i-outline clr-i-outline-path-2"/>
                    <path fill="none" d="M0 0h36v36H0z"/>
                  </svg>
                  <button @click="toggleOfferMenu($event, index)"
                          class="text-white text-xs self-end">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="lucide lucide-ellipsis-vertical-icon lucide-ellipsis-vertical">
                      <circle cx="12" cy="12" r="1"/>
                      <circle cx="12" cy="5" r="1"/>
                      <circle cx="12" cy="19" r="1"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
              class="flex-1 flex flex-col gap-2 bg-slate-900 rounded-md p-2 text-xs divide-y divide-slate-600 overflow-hidden">
            <div class="flex-1 -me-2 pe-1 flex flex-col gap-1 overflow-y-auto custom-scrollbar">
              <div v-for="(value, key) in m.competitors" :key="key" class="flex flex-row justify-between items-center">
                <div class="flex-1 flex flex-row gap-1 items-center">
                  <img v-if="value.profilePicture" :src="value.profilePicture" alt="" class="size-8 rounded-full">
                  <div v-else class="size-8 rounded-full border border-slate-700"></div>
                  <span>{{ key }}</span>
                  <span v-if="value.level" v-tippy="{ content: 'Level', theme: 'my-theme' }">
                    ({{ value.level }})
                  </span>
                  <span v-if="value.quantity" v-tippy="{ content: 'Quantity', theme: 'my-theme' }">
                    {{' - ' + value.quantity}}
                  </span>
                </div>
                <div class="flex flex-row gap-0.5 items-center">
                  <Icon v-if="isMyStore(key)" icon="mdi:star" class="text-yellow-400 size-4"/>
                  $ {{ value.price }}
                </div>
              </div>
            </div>
            <div class="flex flex-col gap-1 pt-2">
              <div v-for="store in m.stores" class="flex flex-row justify-between items-center">
                <div class="flex-1 flex flex-row gap-1 items-center">
                  <span>{{ store.store }}</span>
                  <div class="px-2 py-1 bg-slate-800 flex flex-row gap-1 rounded-md items-center">(#{{ store.offerID }})
                    <span @click="showUpdateStoreModal(m, store)" class="cursor-pointer">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                           stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                           class="lucide lucide-square-pen-icon lucide-square-pen"><path
                          d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path
                          d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/></svg>
                    </span>
                  </div>
                </div>
                <div class="flex flex-row gap-1 justify-between items-center">
                  <span v-if="store.price">$ {{ store.price }}</span>
                  <span @click="showChangePriceModal(store, m)" class="cursor-pointer">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="lucide lucide-square-pen-icon lucide-square-pen"><path
                        d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path
                        d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/></svg>
                  </span>
                </div>
              </div>
            </div>
            <!--          <div class="flex-1 -me-2 pe-1 flex flex-col gap-1 overflow-y-auto custom-scrollbar">
                        <div v-for="(value, key) in m.competitors" :key="key" class="flex flex-row justify-between items-center">
                          <div class="flex-1 flex flex-row gap-1 items-center">
                            <img src="https://picsum.photos/24" alt="" class="size-6 rounded-full">
                            <span>{{ key }} (1)</span>
                          </div>
                          <div class="flex flex-row gap-0.5 items-center">
                            $ {{ value }}
                          </div>
                        </div>
                      </div>
                      <div class="flex flex-col gap-1 pt-2">
                        <div v-for="store in m.stores" class="flex flex-row justify-between items-center">
                          <div class="flex-1 flex flex-row gap-1 items-center">
                            <span>{{ store.store }}</span>
                            <span>(#{{ store.offerID }})</span>
                          </div>
                          <div v-if="store.price" @click="showChangePriceModal" class="flex flex-row gap-1 justify-between items-center">
                            <span>$ {{ store.price }}</span>
                            <span>
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                   class="lucide lucide-square-pen-icon lucide-square-pen"><path
                                  d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path
                                  d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/></svg>
                            </span>
                          </div>
                        </div>
                      </div>-->
          </div>
          <Menu
              :ref="el => (offerMenus[index] = el)"
              :model="getOfferMenuItems(m)"
              :popup="true"
              class="text-xs"
          />
        </div>
      </div>
    </div>

    <AddStoreModal v-if="isShowAddStoreModal" :selectedItem="selectedItem"/>
    <UpdateStoreModal v-if="isShowUpdateStoreModal" :selectedItem="selectedItem" :selectedStore="selectedStore"/>
    <ChangePriceModal v-if="isShowChangePriceModal" :selectedItem="selectedItem" :selectedStore="selectedStore"/>
    <SettingsModal v-if="isShowSettingsModal" :selectedItem="selectedItem" @refresh="refreshData"/>
    <AddMapModal v-if="isShowAddMapModal" @refresh="refreshData"/>
    <FiltersModal v-if="isShowFiltersModal"/>
  </div>
</template>

<style>
.tippy-box[data-theme~='my-theme'] {
  @apply bg-slate-700 text-xs px-2 py-1 text-white rounded-md;
}
</style>